import { defineStore } from 'pinia';
import { ref } from 'vue';

import { isDatadogSyntheticsBrowser } from '@/config';
import { env } from '@/env';
import { useIdentityStore } from '@/stores/identity';
import { loadScript } from '@/utils/dom';
import { getUserTimezone } from '@/utils/timezone';

function createIntercomPlaceholder() {
  const q = [];
  const c = (args) => q.push(args);
  const placeholder = (...args) => c(args);
  placeholder.q = q;
  placeholder.c = c;
  return placeholder;
}

function intercomAction(action, ...options) {
  if (!window.Intercom) return;
  window.Intercom(action, ...options);
}

function startChecklist() {
  window.Intercom('startChecklist', 40367175);
}

function shutdown() {
  intercomAction('shutdown');
}

function reattach() {
  intercomAction('reattach_activator');
}

function update(properties) {
  intercomAction('update', properties);
}

function trackEvent(eventName, properties = {}) {
  intercomAction('trackEvent', eventName, properties);
}

export const useIntercomStore = defineStore('intercom', () => {
  const identityStore = useIdentityStore();
  const showIntercomButtonText = ref(null);
  const ready = ref(false);
  // Visibility is exposed rather than tracked here: the tracking store owns event names, and
  // importing it back created a cycle that made all 1575 specs reaching either store `related` to
  // the other's. `openedFrom`/`buttonText` are captured on show, before onHide clears the source.
  const isOpen = ref(false);
  const openedFrom = ref(null);
  const openedButtonText = ref(null);

  const appId = env.intercomAppId;
  const enabled = env.intercomEnabled && !isDatadogSyntheticsBrowser;

  function show(buttonText = 'Contact Us') {
    showIntercomButtonText.value = buttonText;
    intercomAction('show');
  }

  function boot() {
    const identity = identityStore.identity;
    const currentBrand = identityStore.currentBrand;
    if (enabled && identity && currentBrand) {
      intercomAction('boot', {
        app_id: appId,
        name: `${identity.first_name} ${identity.last_name}`,
        email: identity.email,
        first_name: identity.first_name,
        last_name: identity.last_name,
        has_device: identity.has_device,
        time_zone_name: getUserTimezone(),
        organization: identity.organization?.name || '',
        organization_customer_stage: identity.organization?.customer_stage,
        job_title: identity.job_title,
        is_admin: identity.is_admin,
        brand_id: currentBrand.id,
        brand_name: currentBrand.name,
        avatar: currentBrand.avatarUrl,
        Plan: currentBrand.plan?.plan_type || '',
        brand_customer_stage: currentBrand.plan?.customer_stage,
        prospect_journey: currentBrand.plan?.prospect_journey,
        hide_default_launcher: true,
      });
    }
  }

  if (enabled) {
    loadScript(`https://widget.intercom.io/widget/${appId}`);

    // Inspired by:
    // https://developers.intercom.com/installing-intercom/docs/basic-javascript#how-to-install
    // https://github.com/mathieustan/vue-intercom/blob/master/src/install.js

    if (typeof window.Intercom === 'function') {
      reattach();
      update();
    } else {
      window.Intercom = createIntercomPlaceholder();
    }

    intercomAction('onShow', () => {
      openedButtonText.value = showIntercomButtonText.value;
      openedFrom.value = showIntercomButtonText.value ? 'button' : 'intercom message';
      isOpen.value = true;
    });

    intercomAction('onHide', () => {
      showIntercomButtonText.value = null;
      isOpen.value = false;
    });

    ready.value = true;
  }

  return {
    enabled,
    boot,
    show,
    shutdown,
    trackEvent,
    startChecklist,
    ready,
    update,
    isOpen,
    openedFrom,
    openedButtonText,
  };
});
